/**
 * LogoUpload - Drag-drop logo upload with preview.
 * Per CONTEXT.md: Drag-drop zone with click fallback, saves immediately on upload.
 */

import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Upload, X, Loader2 } from 'lucide-react';

interface LogoUploadProps {
  currentLogoUrl: string | null;
  onUploadComplete: (url: string) => void;
  onRemove: () => void;
  onCropRequest: (file: File) => void;
  isUploading?: boolean;
  disabled?: boolean;
}

export default function LogoUpload({
  currentLogoUrl,
  onUploadComplete: _onUploadComplete,
  onRemove,
  onCropRequest,
  isUploading = false,
  disabled = false,
}: LogoUploadProps) {
  const { t } = useTranslation('companySettings');
  const [isDragging, setIsDragging] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled || isUploading) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFile(files[0]);
    }
  }, [disabled, isUploading]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  }, []);

  const handleFile = (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      return;
    }

    // Send to crop modal
    onCropRequest(file);
  };

  const handleClick = () => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click();
    }
  };

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowRemoveConfirm(true);
  };

  const confirmRemove = () => {
    onRemove();
    setShowRemoveConfirm(false);
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {t('profile.logo.title')}
      </label>

      {/* Drop zone */}
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
        className={`
          relative border-2 border-dashed rounded-lg p-6 transition-colors cursor-pointer
          ${isDragging ? 'border-[#004D40] bg-[var(--pv-color-surface-sunken)]' : 'border-gray-300 hover:border-gray-400'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg"
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled || isUploading}
        />

        <div className="flex items-center gap-4">
          {/* Logo preview or placeholder */}
          <div className="flex-shrink-0 w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden">
            {isUploading ? (
              <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
            ) : currentLogoUrl ? (
              <img
                src={currentLogoUrl}
                alt="Company logo"
                className="w-full h-full object-contain"
              />
            ) : (
              <Building2 className="w-10 h-10 text-gray-400" />
            )}
          </div>

          {/* Instructions */}
          <div className="flex-1">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Upload className="w-4 h-4" />
              <span>{isUploading ? t('profile.logo.uploading') : t('profile.logo.dragDrop')}</span>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {t('profile.logo.restrictions')}
            </p>
          </div>

          {/* Remove button */}
          {currentLogoUrl && !isUploading && (
            <button
              onClick={handleRemoveClick}
              className="flex-shrink-0 p-2 text-gray-400 hover:text-red-500 transition-colors"
              title={t('profile.logo.remove')}
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Remove confirmation dialog */}
      {showRemoveConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('profile.logo.removeConfirm')}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('profile.logo.removeDescription')}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowRemoveConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('unsavedChanges.cancel')}
              </button>
              <button
                onClick={confirmRemove}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                {t('profile.logo.remove')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
